import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Loader2, ExternalLink, Zap, ArrowUpRight, Clock, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SORTS = [
  { id: 'recent', label: 'Most recent', icon: Clock },
  { id: 'deployed', label: 'Deployed live', icon: Globe },
];

function ShareCard({ item }) {
  const ogSrc = `${API}/share/${item.share_id}/og-image.png`;
  return (
    <Link
      to={`/share/${item.share_id}`}
      data-testid={`community-card-${item.share_id}`}
      className="group block rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:border-neutral-300 transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="aspect-[1200/630] bg-gradient-to-br from-slate-900 to-cyan-900 relative overflow-hidden">
        <img
          src={ogSrc}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        {item.has_live_url && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-bold uppercase tracking-wider shadow-md backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold text-neutral-900 text-base truncate flex-1">{item.name}</h3>
          <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-900 transition-colors flex-shrink-0" />
        </div>
        {item.prompt && (
          <p className="text-xs text-neutral-500 line-clamp-2 italic">"{item.prompt}"</p>
        )}
        <div className="flex items-center justify-between pt-1 text-[11px] text-neutral-400">
          <span>by {item.owner_name}</span>
          {item.has_live_url && (
            <a
              href={item.live_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline font-medium"
            >
              live <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CommunityShowcasePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/share/community`, { params: { sort, limit: 60 } });
        if (!cancel) setItems(r.data?.items || []);
      } catch (_) {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [sort]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-neutral-200 bg-gradient-to-br from-cyan-50 via-white to-rose-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-neutral-200 text-xs font-medium text-neutral-700 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-500" /> Community gallery
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-neutral-900">
            Real apps. Real builders.
          </h1>
          <p className="mt-3 text-base text-neutral-600 max-w-2xl">
            Every build below was made by someone like you with a single prompt. Open one to see the
            chat that built it, remix it for yourself, or open the live deployed site.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/build">
              <Button className="h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="community-cta-build">
                <Zap className="w-4 h-4 mr-1.5" /> Start building yours
              </Button>
            </Link>
            <Link to="/showcase" className="text-sm text-neutral-600 hover:text-neutral-900 underline-offset-4 hover:underline">
              Or browse the starter templates →
            </Link>
          </div>
        </div>
      </section>

      {/* Sort tabs */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-8">
        <div className="inline-flex rounded-lg border border-neutral-200 p-1 bg-white">
          {SORTS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSort(id)}
              data-testid={`community-sort-${id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                sort === id ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8 min-h-[400px]">
        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading community builds…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-200 to-sky-300 mb-4">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold text-neutral-900">
              {sort === 'deployed' ? 'No live deploys yet' : 'No public builds yet'}
            </h2>
            <p className="mt-2 text-neutral-500">Be the first — build something and click Share.</p>
            <Link to="/build" className="mt-6 inline-block">
              <Button className="rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
                Start building <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item) => (
              <ShareCard key={item.share_id} item={item} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
